<script setup>
import axios from 'axios';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { shortDate } from '../../../helpers/usDate';
import Loading from '../../Loading.vue';
import XlButton from '../../xl/XlButton.vue';
import XlCard from '../../xl/XlCard.vue';
import XlModal from '../../xl/XlModal.vue';
import XlPagination from '../../xl/XlPagination.vue';
import XlTag from '../../xl/XlTag.vue';
import ContentRowMenu from '../../content/ContentRowMenu.vue';
import ManageAccessModal from '../../resource-access/ManageAccessModal.vue';
import AddedBy from '../../resource-access/AddedBy.vue';
import ResourceAccessTag from '../../resource-access/ResourceAccessTag.vue';
import { bookFor } from '../../../composables/useResourceAccess';
import BannerModal from './BannerModal.vue';
import useHubMembers from '../../../composables/useHubMembers';
import { countOrNull } from '../../../helpers/contentCounts';
import { bannerStatus, serverOffsetMs } from '../../../helpers/bannerHelper';
import { CAPABILITIES, can, isBroker } from '../../../helpers/roles';
import { useAuthStore } from '../../../stores/auth';

/**
 * The management table for banners. Status is derived from the dates on the
 * client with the server's clock offset, so a banner rolls from scheduled to
 * live to ended without a reload; nothing here edits it.
 */
const props = defineProps({
  actionsDisabled: { type: Boolean, default: false },
  // Whether the Banners block is switched on for this hub. The switch lives on
  // the page above; the editor needs it to say whether a save is seen at all.
  blockOn: { type: Boolean, default: true },
});
const emit = defineEmits(['count', 'failed', 'bannerSaved', 'modalClosed', 'editing']);

const PAGE_SIZE = 10;
const TICK_MS = 60000;
const STATUSES = [
  { key: 'all', label: 'All' },
  { key: 'live', label: 'Live' },
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'ended', label: 'Ended' },
];
const TONE_LABEL = { safety: 'Safety', renewal: 'Renewal', announcement: 'Announcement' };
const STATUS_TONE = { live: 'success', scheduled: 'brand', ended: '' };
const STATUS_LABEL = { live: 'Live', scheduled: 'Scheduled', ended: 'Ended' };

const api = () => window.store.event_backend_api;
const notify = (message, error = false) => window.loadNotification?.({ message, error });

const authStore = useAuthStore();
const canManage = computed(() => can(authStore.userRole, CAPABILITIES.CONTRIBUTE_HUB_CONTENT, authStore.capabilities));
// A broker sees every banner that reaches their book but changes only their own.
const mine = (row) => !isBroker(authStore.userData) || row.created_by === authStore.userData?.subdomain_user_id;
const { members, loadHubMembers } = useHubMembers();

const banners = ref([]);
const loading = ref(true);
const failed = ref(false);
const busy = ref(false);
const offsetMs = ref(0);
const nowTick = ref(Date.now());
let tickTimer = null;

const statusFilter = ref('all');
const page = ref(1);

const modalOpen = ref(false);
const modalBanner = ref(null);
const accessTarget = ref(null);
const deleting = ref([]);

// ---- data ----

const load = async () => {
  loading.value = true;
  failed.value = false;
  try {
    const { data } = await axios.get(`${api()}/event-banners`);
    banners.value = data?.data?.banners ?? [];
    if (data?.data?.server_time) offsetMs.value = serverOffsetMs(data.data.server_time);
  } catch (error) {
    console.error(error);
    failed.value = true;
    notify('Could not load the banners.', true);
  } finally {
    loading.value = false;
  }
};

onMounted(async () => {
  tickTimer = setInterval(() => { nowTick.value = Date.now(); }, TICK_MS);
  await load();
  await loadHubMembers();
});
onUnmounted(() => clearInterval(tickTimer));

const nowMs = computed(() => nowTick.value + offsetMs.value);

// Creating the very first banner switches the block on server-side, so an off
// switch at that moment is not something to warn the editor about.
const bannersWillShow = computed(() => props.blockOn || banners.value.length === 0);
const withStatus = computed(() => banners.value.map((b) => ({ ...b, status: bannerStatus(b, nowMs.value) })));

// Ended banners are history, not a count anyone scans for.
watch([withStatus, loading, failed], ([rows, isLoading, hasFailed]) => emit('count', countOrNull(rows.filter((b) => b.status !== 'ended').length, isLoading, hasFailed)), { immediate: true });
watch(failed, (hasFailed) => emit('failed', hasFailed), { immediate: true });
watch(statusFilter, () => { page.value = 1; });

// ---- view ----

// The editor is a page of its own, so the tab tells the shell to stand its
// tabs and block switch down.
const pageEditor = computed(() => modalOpen.value);
watch(pageEditor, (on) => emit('editing', on), { immediate: true });

const stripHtml = (html) => (html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
const when = (iso) => shortDate(iso, { time: true });
const windowText = (b) => (b.ends_at ? `${when(b.starts_at)} → ${when(b.ends_at)}` : `From ${when(b.starts_at)} · no end date`);
const people = (n) => `${new Intl.NumberFormat().format(n)} ${n === 1 ? 'person' : 'people'}`;

const filtered = computed(() => (statusFilter.value === 'all'
  ? withStatus.value
  : withStatus.value.filter((b) => b.status === statusFilter.value)));

const lastPage = computed(() => Math.max(1, Math.ceil(filtered.value.length / PAGE_SIZE)));
const currentPage = computed(() => Math.min(page.value, lastPage.value));
const paged = computed(() => filtered.value.slice((currentPage.value - 1) * PAGE_SIZE, currentPage.value * PAGE_SIZE));
const from = computed(() => (filtered.value.length ? (currentPage.value - 1) * PAGE_SIZE + 1 : 0));
const to = computed(() => Math.min(currentPage.value * PAGE_SIZE, filtered.value.length));

// ---- editor ----

const openAdd = () => {
  if (!canManage.value) return false;
  modalBanner.value = null;
  modalOpen.value = true;
  return true;
};
const openEdit = (row) => {
  if (!canManage.value) return;
  modalBanner.value = banners.value.find((b) => b.id === row.id) ?? row;
  modalOpen.value = true;
};
const closeModal = () => {
  modalOpen.value = false;
  emit('modalClosed');
};
const onSaved = async () => {
  modalOpen.value = false;
  await load();
  emit('bannerSaved');
};

// ---- audience ----

const openAccess = (row) => {
  if (!canManage.value) return;
  accessTarget.value = row;
};
const onAccessSaved = (access) => {
  const row = banners.value.find((b) => b.id === accessTarget.value?.id);
  if (row) {
    row.access = access;
    if (access?.reach !== undefined) row.reach = access.reach;
  }
  accessTarget.value = null;
};

// ---- end now ----

const endNow = async (row) => {
  if (!canManage.value || props.actionsDisabled || busy.value) return;
  busy.value = true;
  // The clock now, not the last minute tick: an end before the banner was
  // saved would read as it never having run (XN-4564).
  const endsAt = new Date(Date.now() + offsetMs.value).toISOString();
  try {
    await axios.put(`${api()}/event-banners/${row.id}`, {
      title: row.title,
      body: row.body,
      cta_label: row.cta_label || null,
      cta_url: row.cta_url || null,
      color: row.color,
      tone: row.tone || null,
      starts_at: row.starts_at,
      ends_at: endsAt,
    });
    const live = banners.value.find((b) => b.id === row.id);
    if (live) live.ends_at = endsAt;
    notify('Banner ended');
  } catch (error) {
    console.error(error);
    notify(messageFrom(error, 'Could not end the banner.'), true);
  } finally {
    busy.value = false;
  }
};

// ---- delete ----

const deleteCost = computed(() => {
  const chosen = deleting.value;
  const reach = chosen.reduce((sum, b) => sum + (b.reach ?? 0), 0);
  const noun = chosen.length === 1 ? 'this banner' : 'these banners';
  if (!reach) return `Nobody sees ${noun}.`;
  return `${people(reach)} will no longer see ${noun}.`;
});

const confirmDelete = (chosen) => {
  if (!canManage.value || props.actionsDisabled || !chosen.length) return;
  deleting.value = chosen;
};

const doDelete = async () => {
  const chosen = deleting.value;
  busy.value = true;
  try {
    await Promise.all(chosen.map((b) => axios.delete(`${api()}/event-banners/${b.id}`)));
    const gone = new Set(chosen.map((b) => b.id));
    banners.value = banners.value.filter((b) => !gone.has(b.id));
    notify(chosen.length === 1 ? 'Banner deleted' : `${chosen.length} banners deleted`);
    deleting.value = [];
  } catch (error) {
    console.error(error);
    notify(messageFrom(error, 'Could not delete.'), true);
    await load();
  } finally {
    busy.value = false;
  }
};

// The editor's own destructive actions: step back to the list, and let it
// ask there, so the confirmation reads the same wherever it was started.
const rowBeingEdited = () => withStatus.value.find((b) => b.id === modalBanner.value?.id) ?? modalBanner.value;

const deleteFromEditor = () => {
  const row = rowBeingEdited();
  modalOpen.value = false;
  if (row) confirmDelete([row]);
};

const endFromEditor = async () => {
  const row = rowBeingEdited();
  modalOpen.value = false;
  if (row) await endNow(row);
};

// ---- row menu ----

// Built per row rather than once: End now only exists while a banner is live.
// Nothing here is for anyone but a manager, so a viewer gets no menu at all
// rather than a menu of controls that would refuse them.
const menuRowId = ref(null);

const menuItems = (row) => (canManage.value
  ? [
    { key: 'access', label: 'Who can see it' },
    { key: 'edit', label: 'Edit' },
    ...(row.status === 'live' ? [{ key: 'endNow', label: 'End now', disabled: props.actionsDisabled || busy.value }] : []),
    { key: 'delete', label: 'Delete', danger: true, disabled: props.actionsDisabled || busy.value },
  ]
  : []);

const onMenu = (row, key) => {
  if (key === 'access') openAccess(row);
  else if (key === 'edit') openEdit(row);
  else if (key === 'endNow') endNow(row);
  else if (key === 'delete') confirmDelete([row]);
};

const messageFrom = (error, fallback) => {
  const message = error?.response?.data?.message;
  if (typeof message === 'string' && message) return message;
  if (message && typeof message === 'object') {
    const first = Object.values(message).flat()[0];
    if (first) return first;
  }
  return fallback;
};

defineExpose({ openAdd });
</script>

<template>
  <div class="banners-tab">
    <BannerModal
      v-if="pageEditor"
      layout="page"
      :banner="modalBanner"
      :live="modalBanner ? withStatus.find((b) => b.id === modalBanner.id)?.status === 'live' : false"
      :members="members"
      :now-offset-ms="offsetMs"
      :banners-visible="bannersWillShow"
      @close="closeModal"
      @saved="onSaved"
      @end-now="endFromEditor"
      @delete="deleteFromEditor"
    />

    <template v-else>
      <div class="banners-tab__toolbar">
        <div class="banners-tab__segment" role="radiogroup" aria-label="Status">
          <button
            v-for="s in STATUSES"
            :key="s.key"
            type="button"
            role="radio"
            :aria-checked="statusFilter === s.key ? 'true' : 'false'"
            :class="{ 'is-on': statusFilter === s.key }"
            @click="statusFilter = s.key"
          >{{ s.label }}</button>
        </div>
        <span class="banners-tab__spacer" />
        <XlButton v-if="canManage" :disabled="actionsDisabled || busy" @click="openAdd">
          <template #icon>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
          </template>
          Add a banner
        </XlButton>
      </div>

      <Loading v-if="loading" variant="rows" :rows="3" />

      <XlCard v-else-if="!banners.length" class="banners-tab__card">
        <div class="banners-tab__empty">
          <b>No banners yet</b>
          <span>Broadcast urgent messages to every policyholder on every hub page.</span>
        </div>
      </XlCard>

      <template v-else>
        <XlCard v-if="!paged.length" class="banners-tab__card">
          <div class="banners-tab__empty">
            <b>No {{ statusFilter === 'all' ? '' : STATUSES.find((s) => s.key === statusFilter).label.toLowerCase() }} banners</b>
            <span>They'll show up here once you add one.</span>
          </div>
        </XlCard>

        <XlCard v-for="row in paged" :key="row.id" class="banners-tab__banner" :class="{ 'is-ended': row.status === 'ended', 'has-menu': menuRowId === row.id }">
          <span class="banners-tab__stripe" :class="`banners-tab__stripe--${row.color}`" :title="row.color" />
          <div class="banners-tab__banner-main">
            <div class="banners-tab__title">
              <b>{{ row.title }}</b>
              <XlTag v-if="TONE_LABEL[row.tone]" class="banners-tab__tone" :class="`banners-tab__tone--${row.color}`">{{ TONE_LABEL[row.tone] }}</XlTag>
              <XlTag :tone="STATUS_TONE[row.status]" class="banners-tab__status" :class="`banners-tab__status--${row.status}`">{{ STATUS_LABEL[row.status] }}</XlTag>
            </div>
            <p class="banners-tab__body">{{ stripHtml(row.body) }}</p>
            <p class="banners-tab__meta">
              <AddedBy :created-by="row.created_by" :name="row.creator_name" />{{ windowText(row) }}<template v-if="row.cta_label"> · button: “{{ row.cta_label }}”</template>
            </p>
          </div>
          <div class="banners-tab__banner-side">
            <ResourceAccessTag :access="row.access" :members="members" :manageable="canManage" :book="bookFor(row, authStore.userData)" @manage="openAccess(row)" />
            <div class="banners-tab__banner-actions">
              <ContentRowMenu v-if="canManage && mine(row)" :items="menuItems(row)" @select="onMenu(row, $event)" @toggle="menuRowId = $event ? row.id : null" />
            </div>
          </div>
        </XlCard>

        <div v-if="lastPage > 1" class="banners-tab__cards-foot">
          <XlPagination :from="from" :to="to" :total="filtered.length" :selected="0" @previous="page = Math.max(1, currentPage - 1)" @next="page = Math.min(lastPage, currentPage + 1)" />
        </div>
      </template>

      <ManageAccessModal
        v-if="accessTarget"
        :resource="{ id: accessTarget.id, name: accessTarget.title ?? accessTarget.name, access: accessTarget.access, created_by: accessTarget.created_by, creator_name: accessTarget.creator_name }"
        :members="members"
        :book="bookFor(accessTarget, authStore.userData)"
        subject-type="banner"
        @close="accessTarget = null"
        @saved="onAccessSaved"
      />

      <XlModal v-if="deleting.length" :title="deleting.length === 1 ? `Delete ${deleting[0].title}?` : `Delete ${deleting.length} banners?`" max-width="480px" @close="deleting = []">
        <p class="banners-tab__cost">{{ deleteCost }}</p>
        <template #actions>
          <XlButton variant="ghost" size="sm" @click="deleting = []">Cancel</XlButton>
          <XlButton variant="danger" size="sm" :disabled="busy" @click="doDelete">Delete</XlButton>
        </template>
      </XlModal>
    </template>
  </div>
</template>

<style scoped>
.banners-tab { display: flex; flex-direction: column; gap: 14px; }

.banners-tab__toolbar { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; }
.banners-tab__spacer { flex: 1; }

.banners-tab__segment { display: inline-flex; padding: 3px; border-radius: var(--xl-radius-pill, 999px); border: 1px solid var(--xl-color-outline-variant, #e7e0ec); background: #fff; }
.banners-tab__segment button {
  border: 0;
  background: transparent;
  font: inherit;
  font-size: var(--xl-type-body-medium-size, 14px);
  color: var(--xl-color-secondary-text, #49454f);
  padding: 7px 14px;
  border-radius: var(--xl-radius-pill, 999px);
  cursor: pointer;
}
.banners-tab__segment button.is-on { background: var(--xl-color-primary, #5e2f78); color: #fff; }

.banners-tab__card { margin-bottom: 0; overflow: hidden; }

.banners-tab__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 48px 16px;
  text-align: center;
  color: var(--xl-color-secondary-text, #49454f);
}
.banners-tab__empty b { color: var(--xl-color-primary-text, #1d1b20); font-size: var(--xl-type-body-large-size, 16px); }

.banners-tab__sort { border: 0; background: transparent; font: inherit; color: inherit; padding: 0; cursor: pointer; display: inline-flex; gap: 4px; align-items: center; }
.banners-tab__sort i { font-style: normal; color: var(--xl-color-secondary-text, #49454f); }

.banners-tab__tick { width: 38px; }
.banners-tab__stripe-col { width: 18px; padding-right: 0; }
.banners-tab__stripe { display: block; width: 6px; height: 34px; border-radius: 3px; }
.banners-tab__stripe--amber { background: #f59e0b; }
.banners-tab__stripe--red { background: #ef4444; }
.banners-tab__stripe--blue { background: #3b82f6; }
.banners-tab__stripe--green { background: #10b981; }
.banners-tab__stripe--purple { background: #8b5cf6; }

.banners-tab__title { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.banners-tab__preview { margin-top: 3px; color: var(--xl-color-secondary-text, #49454f); font-size: var(--xl-type-body-small-size, 12.5px); }
.banners-tab__status--live { animation: banner-pulse 2s ease-in-out infinite; }
@keyframes banner-pulse { 50% { opacity: 0.55; } }

.banners-tab__num { text-align: right; }
.banners-tab__menu { width: 48px; text-align: right; }
.banners-tab__muted { color: var(--xl-color-secondary-text, #49454f); }
.banners-tab__nowrap { white-space: nowrap; }
.banners-tab__none { padding: 30px; text-align: center; color: var(--xl-color-secondary-text, #49454f); }

tr.is-selected td { background: color-mix(in srgb, var(--xl-color-primary, #5e2f78) 6%, white); }
tr.is-ended td { opacity: 0.7; }

.banners-tab__foot { padding: 10px 12px; border-top: 1px solid var(--xl-color-outline-variant, #e7e0ec); }
.banners-tab__cost { margin: 0; font-size: var(--xl-type-body-medium-size, 14px); }
.sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); }

/* ---- the cards layout ---- */

.banners-tab__banner {
  display: flex;
  align-items: center;
  gap: 18px;
  margin-bottom: 0;
  padding: 18px 20px;
}
.banners-tab__banner.is-ended { opacity: 0.7; }
.banners-tab__banner:hover,
.banners-tab__banner.has-menu { background: var(--xl-color-primary-container, #f5eff9); }

.banners-tab__banner .banners-tab__stripe { width: 5px; height: auto; align-self: stretch; min-height: 44px; flex: 0 0 5px; border-radius: 999px; }

/* The tone chip wears the banner's colour, as the prototype does. */
.banners-tab__tone--amber { background: #fdf2e2; color: #8a5200; }
.banners-tab__tone--red { background: #fcebeb; color: #a32020; }
.banners-tab__tone--blue { background: #e7f1f8; color: #1f5e8c; }
.banners-tab__tone--green { background: #e4f3eb; color: #166b48; }
.banners-tab__tone--purple { background: #f5eff9; color: #5e2f78; }

.banners-tab__banner-main { flex: 1; min-width: 0; }
.banners-tab__banner-main .banners-tab__title b { font-size: var(--xl-type-body-large-size, 16px); }

.banners-tab__body {
  margin: 5px 0 0;
  font-size: var(--xl-type-body-medium-size, 14px);
  color: var(--xl-color-secondary-text, #49454f);
}

.banners-tab__meta {
  margin: 6px 0 0;
  font-size: var(--xl-type-body-small-size, 12.5px);
  color: var(--xl-color-secondary-text, #49454f);
}

.banners-tab__banner-side {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}
.banners-tab__banner-actions { display: flex; align-items: center; gap: 10px; }

.banners-tab__cards-foot {
  padding: 10px 12px;
  border: 1px solid var(--xl-color-outline-variant, #e7e0ec);
  border-radius: var(--xl-radius-lg, 16px);
  background: #fff;
}

@media (max-width: 720px) {
  .banners-tab__banner { flex-wrap: wrap; }
  .banners-tab__banner-side { width: 100%; justify-content: space-between; }
}
</style>
